import type { userAgent } from 'next/server';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import logContinueInBrowserClicked from '@alltrails/amplitude/events/logContinueInBrowserClicked';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import logInstallPromptViewed from '@alltrails/amplitude/events/logInstallPromptViewed';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import AppIcon from '@alltrails/denali/components/AppIcon';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import Modal from '@alltrails/modal/components/Modal/Modal';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import useDeeplink from '../../hooks/useDeeplink';
import { setIsOpen } from '../../slices/installBanner';
import { deepLinkRedirect } from '../../utils';
import getBrowserData from '../../utils/getBrowserData';
import MiniInstallInterstitial from '../MiniInstallInterstitial';
import styles from './styles/InstallBanner.module.scss';

type InstallBannerProps = {
  deeplinkData?: DeeplinkData;
  promptLocation: InstallPromptLocation;
  isOpen?: boolean;
  onRequestClose?: () => void;
};

// Design link: https://www.figma.com/file/8Vz0I2E3m5cBiRc0JmLSHJ/Growth-Optimizations-%E2%80%A2-H1?type=design&node-id=149-7294&mode=design&t=3x358HG71vqj9opp-0
// mini_interstitial: https://www.figma.com/design/of9BVPuMqzjbOWZMEDcGxX/New-User-Journey---H1-2026?node-id=5788-176308

const EXPERIMENT_KEY = 'web-growth-rm-mweb-install-drawer-v2';

const InstallBanner = ({ deeplinkData, promptLocation, isOpen: isOpenAttempt = false, onRequestClose }: InstallBannerProps) => {
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const experiments = useExperiments();
  const isTrailPage = promptLocation === 'trail page';
  const experimentValue = isTrailPage ? experiments[EXPERIMENT_KEY]?.value : undefined;
  const isFlip = experimentValue === 'flip';
  const isMiniInterstitial = experimentValue === 'mini_interstitial';
  const isOpen = isOpenAttempt && isMobile;
  const getDeeplink = useDeeplink({ deeplinkData, isOpen: isOpen && !isMiniInterstitial, promptType: 'banner' });

  const ariaLabelElementId = 'install-prompt-label';
  const dispatch = useDispatch();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      dispatch(setIsOpen(isOpen));
    }
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (isOpen && !isMiniInterstitial) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_source: 'native_with_appsflyer_link',
        install_prompt_type: 'banner'
      });
    }
  }, [isMiniInterstitial, isOpen, promptLocation]);

  if (isMiniInterstitial) {
    return (
      <MiniInstallInterstitial
        backgroundClassName={styles.background}
        clickOutsideDeactivates={false}
        deeplinkData={deeplinkData}
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        promptLocation={promptLocation}
        shouldHideCloseButton
        showContinueInBrowser
        variant="app-info"
      />
    );
  }

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_source: 'native_with_appsflyer_link',
      install_prompt_type: 'banner'
    });
    onRequestClose?.();
  };

  const { src, buttonText } = getBrowserData(intl, browserName);

  const continueInAppButton = (
    <button
      className={styles.optionContainer}
      onClick={e => {
        e.stopPropagation();
        deepLinkRedirect(getDeeplink(), 'banner', promptLocation, 'native_with_appsflyer_link');
      }}
    >
      <div className={styles.optionInfo}>
        <AppIcon size={60} />
        <Typography id={ariaLabelElementId} variant="heading200">
          <FormattedMessage defaultMessage="Continue in app" />
        </Typography>
      </div>
      <ChevronSm orientation="right" />
    </button>
  );

  const continueInBrowserButton = (
    <button
      className={styles.optionContainer}
      onClick={e => {
        e.stopPropagation();
        logContinueInBrowserClicked({
          install_prompt_location: promptLocation,
          install_prompt_source: 'native_with_appsflyer_link',
          install_prompt_type: 'banner'
        });
        handleCloseClick();
      }}
    >
      <div className={styles.optionInfo}>
        <div className={styles.browserImageContainer}>{isMounted && <Image alt="" height={40} src={src} width={40} />}</div>
        <Typography variant="heading200">{buttonText}</Typography>
      </div>
      <ChevronSm orientation="right" />
    </button>
  );

  const [firstButton, secondButton] = isFlip ? [continueInBrowserButton, continueInAppButton] : [continueInAppButton, continueInBrowserButton];

  return (
    <Modal
      ariaLabelElementId={ariaLabelElementId}
      backgroundClassName={styles.background}
      focusTrapOptions={{ allowOutsideClick: true, clickOutsideDeactivates: false }}
      initialFocus={false}
      isOpen={isOpen}
      modalClassName={styles.modal}
      onCloseRequest={() => {}}
      shouldHideHeader
    >
      <div className={styles.variantContainer}>
        {firstButton}
        <div className={styles.divider} />
        {secondButton}
      </div>
    </Modal>
  );
};

export default InstallBanner;
